/**
 * BannerPropertiesEditor Component
 * 
 * Property editor for Banner block type.
 */

import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BannerBlockProps } from '@/types/menuBuilder';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBranch } from '@/contexts/BranchContext';
import { useCachedCategories } from '@/hooks/useCache';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BannerPropertiesEditorProps {
  props: BannerBlockProps;
  onUpdate: (props: Partial<BannerBlockProps>) => void;
}

export const BannerPropertiesEditor = memo(function BannerPropertiesEditor({
  props,
  onUpdate,
}: BannerPropertiesEditorProps) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const { selectedBranchId } = useBranch();

  // Fetch categories for dropdown
  const { data: categoriesData, loading: categoriesLoading } = useCachedCategories(
    selectedBranchId || ''
  );
  const categories = categoriesData || [];

  // Fetch all menu items for dropdown
  const { data: menuItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['all-menu-items', selectedBranchId],
    queryFn: async () => {
      if (!selectedBranchId) return [];
      // Use !inner to ensure we filter by branch_id correctly
      const { data, error } = await supabase
        .from('menu_items')
        .select('id, name, category:categories!inner(branch_id)')
        .eq('categories.branch_id', selectedBranchId);
      
      if (error) throw error;
      return data.map(item => ({ id: item.id, name: item.name }));
    },
    enabled: !!selectedBranchId,
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `banner-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('menu-uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('menu-uploads')
        .getPublicUrl(filePath);

      onUpdate({ imageUrl: data.publicUrl });
      toast.success(t('menuBuilder.uploadSuccess', 'Image uploaded successfully'));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('menuBuilder.uploadError', 'Failed to upload image'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleArtUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `art-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('menu-uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('menu-uploads')
        .getPublicUrl(filePath);

      const newArt = {
        id: Math.random().toString(36).substring(2),
        imageUrl: data.publicUrl,
        positionX: 50,
        positionY: 50,
        width: 20,
        rotation: 0,
        opacity: 1,
      };

      onUpdate({ graphicalArts: [...(props.graphicalArts || []), newArt] });
      toast.success(t('menuBuilder.uploadSuccess', 'Art uploaded successfully'));
    } catch (error) {
      console.error('Error uploading art:', error);
      toast.error(t('menuBuilder.uploadError', 'Failed to upload art'));
    } finally {
      setIsUploading(false);
    }
  };

  const updateArt = (id: string, changes: Partial<NonNullable<BannerBlockProps['graphicalArts']>[0]>) => {
    const newArts = (props.graphicalArts || []).map(art => 
      art.id === id ? { ...art, ...changes } : art
    );
    onUpdate({ graphicalArts: newArts });
  };

  const removeArt = (id: string) => {
    const newArts = (props.graphicalArts || []).filter(art => art.id !== id);
    onUpdate({ graphicalArts: newArts });
  };

  return (
    <div className="space-y-6">
      {/* Image Selection */}
      <div className="space-y-3">
        <Label>{t('menuBuilder.bannerImage', 'Banner Image')}</Label>
        
        {props.imageUrl ? (
          <div className="relative rounded-md overflow-hidden border aspect-video group">
            <img 
              src={props.imageUrl} 
              alt="Banner preview" 
              className="w-full h-full object-cover"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onUpdate({ imageUrl: '' })}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-muted/50 transition-colors">
            <input
              type="file"
              id="banner-upload"
              className="hidden"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
            <label 
              htmlFor="banner-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              {isUploading ? (
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-8 w-8 text-muted-foreground" />
              )}
              <span className="text-sm text-muted-foreground">
                {isUploading 
                  ? t('common.uploading', 'Uploading...') 
                  : t('menuBuilder.clickToUpload', 'Click to upload image')
                }
              </span>
            </label>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Input
          value={props.imageUrl || ''}
          onChange={(e) => onUpdate({ imageUrl: e.target.value })}
          placeholder={t('menuBuilder.enterImageUrl', 'Enter image URL...')}
          className="text-xs"
        />
      </div>

      {/* Aspect Ratio */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.aspectRatio', 'Aspect Ratio')}</Label>
        <Select
          value={props.aspectRatio}
          onValueChange={(value) =>
            onUpdate({ aspectRatio: value as BannerBlockProps['aspectRatio'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="16:9">16:9 (Wide)</SelectItem>
            <SelectItem value="4:3">4:3 (Standard)</SelectItem>
            <SelectItem value="3:2">3:2</SelectItem>
            <SelectItem value="2:1">2:1 (Panorama)</SelectItem>
            <SelectItem value="1:1">1:1 (Square)</SelectItem>
            <SelectItem value="9:16">9:16 (Long/Story)</SelectItem>
            <SelectItem value="auto">Original Size</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Link Type */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.linkType', 'Link Type')}</Label>
        <Select
          value={props.linkType}
          onValueChange={(value) =>
            onUpdate({ linkType: value as BannerBlockProps['linkType'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('common.none', 'None')}</SelectItem>
            <SelectItem value="category">{t('menuBuilder.linkToCategory', 'Category')}</SelectItem>
            <SelectItem value="item">{t('menuBuilder.linkToItem', 'Menu Item')}</SelectItem>
            <SelectItem value="external">{t('menuBuilder.externalLink', 'External URL')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Link Target */}
      {props.linkType !== 'none' && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.linkTarget', 'Link Target')}</Label>
          
          {props.linkType === 'category' ? (
            categoriesLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select
                value={props.linkTarget || ''}
                onValueChange={(value) => onUpdate({ linkTarget: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('menuBuilder.selectCategory', 'Select Category')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : props.linkType === 'item' ? (
            itemsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select
                value={props.linkTarget || ''}
                onValueChange={(value) => onUpdate({ linkTarget: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('menuBuilder.selectItem', 'Select Menu Item')} />
                </SelectTrigger>
                <SelectContent>
                  {menuItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : (
            <Input
              value={props.linkTarget || ''}
              onChange={(e) => onUpdate({ linkTarget: e.target.value })}
              placeholder={
                props.linkType === 'external' 
                  ? 'https://...' 
                  : t('menuBuilder.enterIdPlaceholder', 'Enter ID...')
              }
            />
          )}
        </div>
      )}

      {/* Overlay Text */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.overlayText', 'Overlay Text')}</Label>
        <Input
          value={props.overlayText || ''}
          onChange={(e) => onUpdate({ overlayText: e.target.value })}
          placeholder={t('menuBuilder.overlayTextPlaceholder', 'Text on banner...')}
        />
      </div>

      {/* Overlay Position */}
      {props.overlayText && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.overlayPosition', 'Text Position')}</Label>
          <Select
            value={props.overlayPosition || 'bottom'}
            onValueChange={(value) =>
              onUpdate({ overlayPosition: value as BannerBlockProps['overlayPosition'] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="top">{t('common.top', 'Top')}</SelectItem>
              <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
              <SelectItem value="bottom">{t('common.bottom', 'Bottom')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Overlay Opacity (only if image and overlay text) */}
      {props.imageUrl && props.overlayText && (
        <div className="space-y-2">
          <Label>
            {t('menuBuilder.overlayOpacity', 'Overlay Opacity')}: {Math.round((props.overlayOpacity || 0.5) * 100)}%
          </Label>
          <Slider
            value={[(props.overlayOpacity || 0.5) * 100]}
            onValueChange={([value]) => onUpdate({ overlayOpacity: value / 100 })}
            min={0}
            max={80}
            step={5}
          />
        </div>
      )}

      {/* Border Radius */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.borderRadius', 'Border Radius')}</Label>
        <Select
          value={props.borderRadius || 'md'}
          onValueChange={(value) =>
            onUpdate({ borderRadius: value as BannerBlockProps['borderRadius'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('common.none', 'None')}</SelectItem>
            <SelectItem value="sm">{t('common.small', 'Small')}</SelectItem>
            <SelectItem value="md">{t('common.medium', 'Medium')}</SelectItem>
            <SelectItem value="lg">{t('common.large', 'Large')}</SelectItem>
            <SelectItem value="xl">{t('common.extraLarge', 'Extra Large')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Graphical Arts */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <Label>Graphical Arts</Label>
          <div className="relative">
            <input
              type="file"
              id="art-upload"
              className="hidden"
              accept="image/*"
              onChange={handleArtUpload}
              disabled={isUploading}
            />
            <label 
              htmlFor="art-upload"
              className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Add Art
            </label>
          </div>
        </div>

        <div className="space-y-4">
          {props.graphicalArts?.map((art, index) => (
            <div key={art.id} className="border rounded-md p-3 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded overflow-hidden bg-muted border">
                  <img src={art.imageUrl} alt="Art" className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 text-sm font-medium">Art #{index + 1}</div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeArt(art.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Position X (%)</Label>
                  <Input
                    type="number"
                    value={art.positionX}
                    onChange={(e) => updateArt(art.id, { positionX: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Position Y (%)</Label>
                  <Input
                    type="number"
                    value={art.positionY}
                    onChange={(e) => updateArt(art.id, { positionY: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Width (%)</Label>
                  <Input
                    type="number"
                    value={art.width}
                    onChange={(e) => updateArt(art.id, { width: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rotation (deg)</Label>
                  <Input
                    type="number"
                    value={art.rotation || 0}
                    onChange={(e) => updateArt(art.id, { rotation: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {(!props.graphicalArts || props.graphicalArts.length === 0) && (
            <div className="text-center text-sm text-muted-foreground py-4">
              No graphical arts added yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default BannerPropertiesEditor;
